/**
 * useShiftCopying Hook
 * 
 * Refactored from 456 lines to ~150 lines by extracting pure functions
 * into utility modules. Focuses on state management and React-specific logic.
 */

import { useState, useCallback } from 'react';
import { toast } from "@/hooks/ui/use-toast";
import { DayShift, Shift } from '@/types';

// Import pure utility functions
import {
  validateCopyingParameters,
  getValidTargetDates,
  filterDatesByWeekdays,
  parseWeekdayString,
  createShiftInsertRecords,
  generateCopySummary,
  formatDateForLogging,
  findMissingDates,
  CopyResult,
} from '@/lib/scheduling/shift-copying-utils';

import {
  batchCopyShifts,
  validateCopiedShifts,
} from '@/lib/scheduling/shift-copying-db';

interface UseShiftCopyingProps {
  businessId: string | null;
  onShiftCreated: () => void;
}

type CopyingType = 'all-days' | 'weekdays' | 'selected-dates' | null;

interface UseShiftCopyingReturn {
  copiedShift: Shift[] | null;
  setCopiedShift: (shifts: Shift[] | null) => void;
  isCopying: boolean;
  copyingType: CopyingType;
  copyShiftsToAllDaysInMonth: (selectedDate: Date, dayShifts: DayShift[]) => Promise<void>;
  copyShiftsToWeekdaysInMonth: (selectedDate: Date, dayShifts: DayShift[], selectedWeekdayString: string) => Promise<void>;
  copyShiftsToSelectedDates: (selectedDates: Date[], dayShifts: DayShift[]) => Promise<void>;
  pasteShift: (targetDay: Date, shiftsToPaste: Shift[]) => Promise<void>;
}

export const useShiftCopying = ({
  businessId,
  onShiftCreated,
}: UseShiftCopyingProps): UseShiftCopyingReturn => {
  const [copiedShift, setCopiedShift] = useState<Shift[] | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [copyingType, setCopyingType] = useState<CopyingType>(null);

  /**
   * Generic copy function that handles the core copying logic
   */
  const performCopyOperation = useCallback(async (
    selectedDate: Date,
    dayShifts: DayShift[],
    targetDates: Date[],
    operationType: string,
    copyType: CopyingType
  ) => {
    // Validate parameters
    const validation = validateCopyingParameters(selectedDate, businessId, dayShifts);
    if (!validation.isValid) {
      toast.error(validation.error || 'Invalid copying parameters');
      return;
    }

    if (targetDates.length === 0) {
      toast.error(`No valid target dates found for ${operationType}.`);
      return;
    }

    setIsCopying(true);
    setCopyingType(copyType);

    try {
      // Create template shifts for insertion
      const shiftsTemplate = createShiftInsertRecords(dayShifts, targetDates[0], businessId!);

      // Perform batch copy operation
      const batchResult = await batchCopyShifts(
        targetDates,
        shiftsTemplate,
        (completed, total, currentDate) => {
          // Progress: copying to target dates
        }
      );

      // Validate results
      const validationResult = await validateCopiedShifts(businessId!, selectedDate);
      const missingDates = findMissingDates(targetDates, validationResult.startTimes);

      // Create result summary
      const result: CopyResult = {
        copiedCount: batchResult.successCount,
        errorCount: batchResult.errorCount,
        skippedCount: 0, // Past dates are already filtered out
        missingDates,
      };

      // Generate and display summary
      const summary = generateCopySummary(result, operationType, selectedDate);
      
      if (summary.isSuccess) {
        toast.success(summary.title, { description: summary.description });
      } else {
        toast.error(summary.title, { description: summary.description });
      }

      // Track errors for debugging
      if (batchResult.errors.length > 0) {
        // Copy operation had errors
      }

      // Trigger refresh
      onShiftCreated();

    } catch (error) {
      toast.error(`${operationType} Failed`, {
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    } finally {
      setIsCopying(false);
      setCopyingType(null);
    }
  }, [businessId, onShiftCreated]);

  /**
   * Copy shifts to all valid days in the month
   */
  const copyShiftsToAllDaysInMonth = useCallback(async (
    selectedDate: Date,
    dayShifts: DayShift[]
  ) => {
    const targetDates = getValidTargetDates(selectedDate);
    await performCopyOperation(
      selectedDate,
      dayShifts,
      targetDates,
      'Copy to All Days',
      'all-days'
    );
  }, [performCopyOperation]);

  /**
   * Copy shifts to specific weekdays in the month
   */
  const copyShiftsToWeekdaysInMonth = useCallback(async (
    selectedDate: Date,
    dayShifts: DayShift[],
    selectedWeekdayString: string
  ) => {
    const allTargetDates = getValidTargetDates(selectedDate);
    const weekdays = parseWeekdayString(selectedWeekdayString);
    const targetDates = filterDatesByWeekdays(allTargetDates, weekdays);
    
    await performCopyOperation(
      selectedDate,
      dayShifts,
      targetDates,
      `Copy to ${selectedWeekdayString}`,
      'weekdays'
    );
  }, [performCopyOperation]);

  /**
   * Copy shifts to selected custom dates
   */
  const copyShiftsToSelectedDates = useCallback(async (
    selectedDates: Date[],
    dayShifts: DayShift[]
  ) => {
    if (selectedDates.length === 0 || dayShifts.length === 0) {
      toast.error('No dates selected or no shifts to copy');
      return;
    }

    const sourceDate = dayShifts[0]?.startTime || new Date();
    await performCopyOperation(
      sourceDate,
      dayShifts,
      selectedDates,
      'Copy to Selected Dates',
      'selected-dates'
    );
  }, [performCopyOperation]);

  /**
   * Legacy paste function for backward compatibility
   */
  const pasteShift = useCallback(async (targetDay: Date, shiftsToPaste: Shift[]) => {
    if (!businessId || shiftsToPaste.length === 0) {
      toast.error('No shifts to paste or missing business ID');
      return;
    }

    try {
      // Convert Shift[] to DayShift[] format
      const dayShifts: DayShift[] = shiftsToPaste.map(shift => ({
        id: shift.id,
        startTime: new Date(shift.start_time),
        endTime: new Date(shift.end_time),
        title: shift.title,
        employeeId: shift.employee_id,
        employeeName: null,
        isNew: false,
        isEditing: false,
        requestCount: 0,
        breaks: shift.breaks,
      }));

      await copyShiftsToSelectedDates([targetDay], dayShifts);
    } catch (error) {
      toast.error('Paste Failed', {
        description: 'Failed to paste shifts to the target date'
      });
    }
  }, [businessId, copyShiftsToSelectedDates]);

  return {
    copiedShift,
    setCopiedShift,
    isCopying,
    copyingType,
    copyShiftsToAllDaysInMonth,
    copyShiftsToWeekdaysInMonth,
    copyShiftsToSelectedDates,
    pasteShift,
  };
};